import React, { useEffect, useState } from "react";
import { Box, Typography, Modal, CircularProgress } from "@mui/material";
import { ModalDrawerStyles, ModalHeader } from "../../Common/styles/modal";
import http from "../../../utils/http";
import { DateTime } from "luxon";
import { errorToastMessage } from "../../../utils/toast";
import { NoDataContainer } from "../../Common/styles/table";
import { AxiosResponse } from "axios";

interface NoteDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  studyId?: string;
}

const ViewNoteDetailsModal: React.FC<NoteDetailsModalProps> = ({
  open,
  onClose,
  data,
  studyId,
}) => {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<any[]>([]);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        let res: AxiosResponse;
        if (studyId) {
          res = await http.get(
            `/participant-notes?studyId=${studyId}&participantId=${data?.id}`
          );
        } else {
          res = await http.get(`/participant-notes/${data?.id}`);
        }
        const newData = res.data?.data?.map((item: any) => ({
          id: item.id,
          addedBy: item.createdByUser
            ? `${item.createdByUser.firstName || ""} ${
                item.createdByUser.lastName || ""
              }`.trim()
            : "",
          date: item.createdAt
            ? DateTime.fromISO(item.createdAt).toFormat("dd-MM-yyyy hh:mm a")
            : "",
          visibility: item.isPublic ? "global" : "study",
          note: item.content,
          studyId: item.studyId,
          editable: item.studyId === studyId,
          studyName: item?.study?.name,
        }));
        setNotes(newData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        errorToastMessage(error as Error);
      }
    };
    fetchNotes();
  }, [studyId, data?.id]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Notes" onCloseClick={onClose} />

        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            overflow: "auto",
            pr: 0.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {!loading && notes?.length > 0 && (
              <>
                {notes?.map((note: any) => (
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #E0E3EB",
                      borderRadius: "10px",
                    }}
                    key={note.id}
                  >
                    <Box sx={{ mb: 3, display: "flex", gap: 8 }}>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={"regular"}
                          sx={{ mb: 1 }}
                        >
                          Added By
                        </Typography>
                        <Typography variant="subtitle1">
                          {note.addedBy || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={"regular"}
                          sx={{ mb: 1 }}
                        >
                          Date
                        </Typography>
                        <Typography variant="subtitle1">
                          {note.date || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={"regular"}
                          sx={{ mb: 1 }}
                        >
                          Visibility
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          textTransform="capitalize"
                        >
                          {note.visibility || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={"regular"}
                          sx={{ mb: 1 }}
                        >
                          Study
                        </Typography>
                        <Typography variant="subtitle1">
                          {note.studyName || "-"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        fontWeight="regular"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {note.note || "-"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </>
            )}

            {!loading && notes?.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
            {loading && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress size={25} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ViewNoteDetailsModal;
